/**
 * Audit Logs Page — 감사 로그
 * 접근: 제품 관리자(productAdmin)만
 *
 * embedded=true : SystemSettingsPage 탭 안에 삽입
 * embedded=false: /admin/audit-logs 독립 페이지
 */

import { useState, useEffect, useCallback } from 'react';
import { Topbar } from '../components/layout';
import { Badge, Pagination, SearchInput, Select, Tabs, useToast } from '../components/ui';
import type { TabItem, BadgeColor } from '../components/ui';
import { logsApi, type AuditLog } from '../api/logs';
import { authApi, tenantApi, customerApi } from '../api';
import { isProductAdminRole } from '../utils/role';
import '../styles/adminPage.css';

// ── 필터 탭 ───────────────────────────────────────────────────────────────────

type ActionFilter =
  | 'all'
  | 'auth'
  | 'workflow'
  | 'kb'
  | 'user'
  | 'provider'
  | 'connector'
  | 'org';

const ACTION_TABS: TabItem<ActionFilter>[] = [
  { key: 'all',       label: '전체' },
  { key: 'auth',      label: '로그인' },
  { key: 'workflow',  label: '워크플로우' },
  { key: 'kb',        label: '지식베이스' },
  { key: 'user',      label: '사용자' },
  { key: 'org',       label: '조직관리' },
  { key: 'provider',  label: 'AI 설정' },
  { key: 'connector', label: '커넥터' },
];

const ACTION_PREFIX: Record<ActionFilter, string> = {
  all:       '',
  auth:      'login',
  workflow:  'workflow.',
  kb:        'kb.',
  user:      'user.',
  org:       'tenant.',   // 필터 로직에서 tenant. | customer. 처리
  provider:  'provider.',
  connector: 'connector.',
};

// ── 액션 라벨 ─────────────────────────────────────────────────────────────────

const AUDIT_ACTION_LABEL: Record<string, { label: string; color: BadgeColor }> = {
  'login':                      { label: '로그인',          color: 'info' },
  'logout':                     { label: '로그아웃',        color: 'gray' },
  'workflow.create':            { label: '워크플로우 생성',  color: 'primary' },
  'workflow.update':            { label: '워크플로우 수정',  color: 'primary' },
  'workflow.deploy':            { label: '워크플로우 배포',  color: 'success' },
  'workflow.delete':            { label: '워크플로우 삭제',  color: 'error' },
  'workflow.run':               { label: '워크플로우 실행',  color: 'medium' },
  'workflow.schedule.pause':    { label: '스케줄 정지',      color: 'medium' },
  'workflow.schedule.resume':   { label: '스케줄 재가동',    color: 'success' },
  'user.create':                { label: '사용자 등록',      color: 'success' },
  'user.update':                { label: '사용자 수정',     color: 'primary' },
  'user.delete':                { label: '사용자 삭제',     color: 'error' },
  'tenant.create':              { label: '테넌트 생성',      color: 'primary' },
  'tenant.update':              { label: '테넌트 수정',      color: 'primary' },
  'tenant.delete':              { label: '테넌트 삭제',      color: 'error' },
  'customer.create':            { label: '고객사 생성',      color: 'primary' },
  'customer.update':            { label: '고객사 수정',      color: 'primary' },
  'customer.delete':            { label: '고객사 삭제',      color: 'error' },
  'kb.sync':                    { label: 'KB 동기화',       color: 'info' },
  'kb.create':                  { label: 'KB 생성',         color: 'primary' },
  'kb.update':                  { label: 'KB 수정',         color: 'primary' },
  'kb.delete':                  { label: 'KB 삭제',         color: 'error' },
  'provider.apikey.save':       { label: 'API Key 등록',    color: 'success' },
  'provider.apikey.delete':     { label: 'API Key 삭제',    color: 'error' },
  'provider.bedrock.create':    { label: 'Bedrock 계정 추가', color: 'primary' },
  'provider.bedrock.update':    { label: 'Bedrock 계정 수정', color: 'primary' },
  'provider.bedrock.delete':    { label: 'Bedrock 계정 삭제', color: 'error' },
  'provider.test':              { label: '연결 테스트',      color: 'info' },
  'connector.connect':          { label: '커넥터 연결',     color: 'success' },
  'connector.disconnect':       { label: '커넥터 해제',     color: 'error' },
  'system.settings':            { label: 'AI 설정',        color: 'medium' },
};

// ── 날짜 포맷 ─────────────────────────────────────────────────────────────────

function fmtDatetime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' });
  const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${date} ${time}`;
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function AuditLogsPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);
  const PAGE_SIZE = 15;

  const currentUser = authApi.getUser();
  const isProductAdmin = isProductAdminRole(currentUser?.role);

  // 이메일 → 이름 매핑
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(new Map());
  // 테넌트/고객사 ID → 이름 매핑 (감사 로그 내용의 UUID 변환용)
  const [tenantNameMap, setTenantNameMap] = useState<Map<string, string>>(new Map());
  const [customerNameMap, setCustomerNameMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    authApi.listUsers().then(users => {
      const map = new Map<string, string>();
      users.forEach(u => {
        if (u.email) map.set(u.email, u.name || u.email);
        if (u.id)    map.set(u.id,    u.name || u.email);
      });
      setUserNameMap(map);
    }).catch(() => {});

    tenantApi.list().then(ts => {
      setTenantNameMap(new Map(ts.map(t => [t.id, t.name])));
      if (isProductAdmin) setTenants(ts);
    }).catch(() => {});

    customerApi.list().then(cs => {
      setCustomerNameMap(new Map(cs.map(c => [c.id, c.name])));
    }).catch(() => {});
  }, []);


  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await logsApi.getAuditLogs({ limit: 500, tenantId: tenantFilter || undefined });
      setLogs(data);
    } catch {
      showToast('error', '감사 로그를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast, tenantFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [search, actionFilter, dateFrom, dateTo, tenantFilter]);

  const filtered = logs.filter((l) => {
    // 날짜 범위 필터
    if (dateFrom && l.createdAt < dateFrom) return false;
    if (dateTo   && l.createdAt > dateTo + 'T23:59:59') return false;
    // 액션 카테고리 필터
    if (actionFilter !== 'all') {
      const prefix = ACTION_PREFIX[actionFilter];
      // AI 설정 탭은 provider.* 과 system.settings 모두 포함
      if (actionFilter === 'provider') {
        if (!l.action.startsWith('provider.') && l.action !== 'system.settings') return false;
      } else if (actionFilter === 'org') {
        if (!l.action.startsWith('tenant.') && !l.action.startsWith('customer.')) return false;
      } else {
        if (!l.action.startsWith(prefix)) return false;
      }
    }
    // 검색어 필터
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      l.userEmail?.toLowerCase().includes(q) ||
      l.action?.toLowerCase().includes(q) ||
      l.target?.toLowerCase().includes(q) ||
      l.ip?.includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged      = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // 검색바 (사용자 관리와 동일한 구조)
  const searchBar = (
    <div className="users-search-bar">
      <SearchInput
        placeholder="사용자, 액션, 대상으로 검색..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onClear={() => setSearch('')}
      />
      <button
        className="users-search-refresh"
        onClick={() => load(true)}
        disabled={refreshing}
        title="새로고침"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
        </svg>
      </button>
    </div>
  );

  const dateFilter = (
    <div className="audit-date-range">
      <input
        type="date"
        className="input-control audit-date-input"
        value={dateFrom}
        onChange={e => setDateFrom(e.target.value)}
        title="시작 날짜"
      />
      <span className="audit-date-sep">~</span>
      <input
        type="date"
        className="input-control audit-date-input"
        value={dateTo}
        onChange={e => setDateTo(e.target.value)}
        title="종료 날짜"
      />
    </div>
  );

  const body = (
    <>
      {/* 탭 + 필터 줄 — 넓은 화면: 한 줄 / 좁은 화면: 필터가 아래로 wrap */}
      <div className="tabs-row tabs-row--wrap">
        <Tabs items={ACTION_TABS} active={actionFilter} onChange={setActionFilter} />
        <div className="tabs-row-aside audit-filter-bar">
          {isProductAdmin && (
            <Select value={tenantFilter} onChange={e => { setTenantFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">전체 테넌트</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          )}
          {dateFilter}
          {searchBar}
        </div>
      </div>

      <div className="admin-section">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : filtered.length === 0 ? (
          <div className="logs-empty">
            <p className="logs-empty-title">
              {logs.length === 0 ? '감사 로그가 없습니다' : '검색 결과가 없습니다'}
            </p>
            {logs.length === 0 && (
              <p className="logs-empty-desc">사용자 액션이 기록되면 여기에 표시됩니다.</p>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>타임스탬프</th>
                  <th>사용자</th>
                  {isProductAdmin && !tenantFilter && <th>테넌트</th>}
                  <th>액션</th>
                  <th>대상</th>
                  <th>내용</th>
                  <th>결과</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((log) => {
                  const action = AUDIT_ACTION_LABEL[log.action] ?? { label: log.action, color: 'gray' as BadgeColor };
                  const details = log.details;
                  const changeKeys = details?.before ? Object.keys(details.before) : [];

                  // UUID처럼 보이는 값을 이름으로 변환하는 헬퍼
                  const fmtDetailVal = (key: string, val: string) => {
                    if (key === '테넌트') return tenantNameMap.get(val) || val;
                    if (key === '고객사') return customerNameMap.get(val) || val;
                    return val;
                  };
                  return (
                    <tr key={log.logId}>
                      <td className="left mono mute" style={{ whiteSpace: 'nowrap' }}>
                        {fmtDatetime(log.createdAt)}
                      </td>
                      <td className="left"
                        title={log.userEmail || log.userId}>
                        {userNameMap.get(log.userEmail || '') || log.userEmail || log.userId}
                      </td>
                      {isProductAdmin && !tenantFilter && (
                        <td className="left mute" style={{ fontSize: 12 }}>
                          {log.tenantId ? (tenantNameMap.get(log.tenantId) || log.tenantId) : '-'}
                        </td>
                      )}
                      <td className="left">
                        <Badge color={action.color} shape="tag">{action.label}</Badge>
                      </td>
                      <td className="left mute">{log.target || '-'}</td>
                      <td className="left">
                        {/* 실패: 오류 메시지 표시 */}
                        {log.result === 'failure' && log.errorMessage ? (
                          <span className="audit-error-msg" title={log.errorMessage}>
                            {log.errorMessage.length > 60 ? log.errorMessage.slice(0, 60) + '…' : log.errorMessage}
                          </span>
                        ) : changeKeys.length > 0 ? (
                          <div className="audit-changes">
                            {changeKeys.map((key) => (
                              <span key={key} className="audit-change-item">
                                <span className="audit-change-key">{key}</span>
                                <span className="audit-change-before">{fmtDetailVal(key, String(details!.before![key] ?? '-'))}</span>
                                <span className="audit-change-arrow">→</span>
                                <span className="audit-change-after">{fmtDetailVal(key, String(details!.after?.[key] ?? '-'))}</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="mute">-</span>
                        )}
                      </td>
                      <td>
                        <Badge color={log.result === 'success' ? 'success' : 'error'} shape="tag">
                          {log.result === 'success' ? '성공' : '실패'}
                        </Badge>
                      </td>
                      <td className="left mono mute">{log.ip || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 페이지네이션 (공통) */}
      {!loading && totalPages > 1 && (
        <Pagination
          current={currentPage}
          total={totalPages}
          onChange={setCurrentPage}
          info={<><b>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)}</b> / {filtered.length}건</>}
        />
      )}
    </>
  );

  if (embedded) return body;

  return (
    <>
      <Topbar title="감사 로그" description="사용자 액션 전체 이력을 조회합니다" />
      <div className="page-content">{body}</div>
    </>
  );
}

export default AuditLogsPage;
